/**
 * htmlInit.js — All inline JS from index.html extracted for CSP compliance.
 *
 * Contains:
 *   1. Global error handlers + mobile init state
 *   2. Dropdown menu system
 *   3. Right sidebar two-tier tabs toggle
 *   4. Floating sidebar logic (Stage 3: viewport-first)
 *   5. Toolbar auto-collapse
 *   6. Edge triggers + keyboard shortcuts
 */

(function initHtmlBindings() {
  'use strict';

  // ══════════════════════════════════════════════════════════════
  // 1. GLOBAL ERROR HANDLERS + MOBILE INIT STATE
  // ══════════════════════════════════════════════════════════════

  window._mobileActiveTool = 'WindowLevel';
  window._userHasInteracted = false;

  ['pointerdown', 'touchstart'].forEach(function(ev) {
    document.addEventListener(ev, function() {
      window._userHasInteracted = true;
    }, { once: true, passive: true });
  });

  window.onerror = function(msg, src, line, col, err) {
    let msgStr = (msg || '').toString();
    if (msgStr.indexOf('MagnifyTool only works on StackViewports') !== -1) return true;
    console.error('[Global Error]', msg, 'at', src, 'line:', line);
  };

  window.addEventListener('unhandledrejection', function(e) {
    console.error('[Unhandled Promise]', e.reason);
  });

  // ══════════════════════════════════════════════════════════════
  // 2. DROPDOWN MENU SYSTEM
  // ══════════════════════════════════════════════════════════════

  document.addEventListener('click', function(e) {
    let trigger = e.target.closest('.tb-dropdown-trigger');
    if (trigger) {
      e.stopPropagation();
      let dd = trigger.closest('.tb-dropdown');
      // [7.0.19] Кнопка с классом .tb-dropdown-trigger, но БЕЗ обёртки .tb-dropdown —
      // это не раскрывающееся меню, а обычная кнопка-действие. Так сделаны
      // «✅ Протокол» и «🔬 Качество МРТ»: класс взят ради оформления, стрелка
      // погашена через ::after{content:none}. Раньше здесь безусловно читалось
      // dd.classList — при dd === null это TypeError на КАЖДОМ клике по такой
      // кнопке. Само действие срабатывало (обработчик на элементе успевает
      // отработать раньше document), но stopPropagation() уже прошёл, а цикл
      // закрытия меню — нет: ранее открытое меню оставалось висеть на экране.
      // Плюс исключение сорило в консоль ровно там, где мы ищем строки [GATE].
      if (!dd) {
        document.querySelectorAll('.tb-dropdown.open').forEach(function(d) {
          d.classList.remove('open');
        });
        return;
      }
      let wasOpen = dd.classList.contains('open');
      document.querySelectorAll('.tb-dropdown.open').forEach(function(d) {
        d.classList.remove('open');
      });
      if (!wasOpen) dd.classList.add('open');
      return;
    }
    // [FIX] Клик внутри открытого меню (select, label и т.д.) — не закрываем
    if (e.target.closest('.tb-dropdown-menu')) {
      // Закрываем только если кликнули по .tb-btn (кнопке действия)
      let menuBtn = e.target.closest('.tb-dropdown-menu .tb-btn');
      if (menuBtn) {
        setTimeout(function() {
          document.querySelectorAll('.tb-dropdown.open').forEach(function(d) {
            d.classList.remove('open');
          });
        }, 80);
      }
      return;
    }
    document.querySelectorAll('.tb-dropdown.open').forEach(function(d) {
      d.classList.remove('open');
    });
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      document.querySelectorAll('.tb-dropdown.open').forEach(function(d) {
        d.classList.remove('open');
      });
    }
  });

  // ══════════════════════════════════════════════════════════════
  // 3. RIGHT SIDEBAR: TWO-TIER TABS TOGGLE
  // ══════════════════════════════════════════════════════════════

  let toggleBtn = document.getElementById('right-tabs-expand');
  let overflow = document.getElementById('right-tabs-overflow');

  if (toggleBtn && overflow) {
    toggleBtn.addEventListener('click', function() {
      let isOpen = overflow.classList.toggle('open');
      toggleBtn.classList.toggle('open', isOpen);
      toggleBtn.setAttribute('aria-expanded', isOpen);
    });
    overflow.addEventListener('click', function(e) {
      let tab = e.target.closest('.right-tab');
      if (tab) {
        overflow.classList.add('open');
        toggleBtn.classList.add('open');
        toggleBtn.setAttribute('aria-expanded', 'true');
      }
    });
  }

  // ══════════════════════════════════════════════════════════════
  // 4. FLOATING SIDEBARS — desktop only (>768px)
  //    Uses capture phase to intercept BEFORE sidebarBindings.js
  //    Clears inline style.display to let CSS !important work
  // ══════════════════════════════════════════════════════════════

  if (window.innerWidth > 768) {
    let sideL = document.getElementById('sidebar-left');
    let sideR = document.getElementById('sidebar-right');
    let backdrop = document.getElementById('desktop-backdrop');
    let btnL = document.getElementById('btn-sidebar-left-toggle');
    let btnR = document.getElementById('btn-sidebar-right-toggle');
    let edgeL = document.getElementById('edge-left');
    let edgeR = document.getElementById('edge-right');
    let btnTB = document.getElementById('btn-toolbar-toggle');
    let toolbar = document.getElementById('toolbar');

    function clearInlineDisplay(el) {
      if (el) el.style.display = '';
    }

    function closeSidebars() {
      // [PIN] Закреплённая панель не закрывается кликами и Escape.
      // [10.1.3] Правая теперь тоже: раньше её закрывало открытие левой, и
      // держать обе открытыми было нельзя — хотя на широком экране это как
      // раз обычный рабочий вид.
      let pinnedL = sideL && sideL.classList.contains('pinned');
      let pinnedR = sideR && sideR.classList.contains('pinned');
      if (sideL && !pinnedL) { sideL.classList.remove('floating-open'); clearInlineDisplay(sideL); }
      if (sideR && !pinnedR) { sideR.classList.remove('floating-open'); clearInlineDisplay(sideR); }
      if (backdrop) backdrop.classList.remove('active');
      if (btnL && !pinnedL) btnL.classList.remove('panel-active');
      if (btnR && !pinnedR) btnR.classList.remove('panel-active');
    }

    /**
     * [10.1.6] Панель открывается ВСТРОЕННОЙ в раскладку, а не накладкой.
     *
     * Вот в чём была настоящая беда, из-за которой «сужение окон» не
     * работало три версии подряд. Я чинил ширину центра и правила CSS, а
     * ломалось не там: открытие панели ставило только класс floating-open и
     * НЕ возвращало встроенный режим. Поэтому после первого же закрытия
     * панель открывалась заново как накладка поверх изображения — окна
     * оставались широкими, и правый край картинки уходил под панель.
     *
     * Измерено: панель закрыли — окно 694 → 834, открыли обратно — окно так
     * и осталось 834 вместо 694. Ровно та полоса, которую вы видели срезанной.
     *
     * Теперь открытие всегда встраивает панель: окна ужимаются, изображение
     * целиком остаётся на виду. Занавес (тёмный фон) при этом не нужен —
     * панель не перекрывает работу, а стоит рядом.
     */
    function openSide(side) {
      const el = side === 'L' ? sideL : sideR;
      const btn = side === 'L' ? btnL : btnR;
      if (!el) return;
      closeSidebars();
      clearInlineDisplay(el);
      el.classList.add('floating-open', 'pinned');
      document.body.classList.add(side === 'L' ? 'sbL-pinned' : 'sbR-pinned');
      if (btn) btn.classList.add('panel-active');
      if (side === 'L' && pinBtnRef) pinBtnRef.classList.add('active');
      if (backdrop) backdrop.classList.remove('active');
      try { localStorage.setItem(side === 'L' ? 'ui.sbL.pinned' : 'ui.sbR.pinned', '1'); } catch (e) { /* приватный режим */ }
      // Ширина окон изменилась — движку нужно не просто пересчитать канвы, но
      // и заново вписать изображение: подробности в relayoutViewports.
      _relayout();
    }

    /**
     * Пересчёт после смены ширины окон.
     *
     * Обычного события resize мало. Его обработчик зовёт re.resize(true, true),
     * где второй аргумент — keepCamera: движок восстанавливает ПРЕЖНЮЮ камеру,
     * посчитанную для широкого окна. Канва ужимается, а изображение остаётся в
     * старом масштабе и вылезает за край. Здесь нужно именно вписать заново.
     */
    function _relayout() {
      setTimeout(() => {
        try { window.MikhmelViewer?.relayoutViewports?.(); }
        catch (e) { /* движок ещё не поднялся — тогда и вписывать нечего */ }
        window.dispatchEvent(new Event('resize'));
      }, 320);   // после перехода панели (0.3 с), иначе меряем старую ширину
    }

    function openLeft() { openSide('L'); }
    function openRight() { openSide('R'); }

    /**
     * [10.1.4] Кнопки панелей в верхнем ряду перестали работать после того,
     * как обе панели стали закреплёнными по умолчанию (10.1.3).
     *
     * Логика была такая: нажали — если панель открыта, зовём closeSidebars().
     * А closeSidebars нарочно ЩАДИТ закреплённые панели, чтобы их не сбивало
     * случайным кликом мимо или клавишей Escape. Панель закреплена всегда —
     * значит закрытие не срабатывало никогда, и кнопка выглядела сломанной.
     *
     * Разделяю два случая. Случайное закрытие (клик по фону, Escape) —
     * по-прежнему не трогает закреплённую панель. А нажатие на саму кнопку
     * панели — намеренное действие, и оно закрывает независимо от булавки,
     * заодно снимая её и запоминая выбор.
     */
    function forceClose(side) {
      const el = side === 'L' ? sideL : sideR;
      if (!el) return;
      el.classList.remove('pinned', 'floating-open');
      clearInlineDisplay(el);
      document.body.classList.remove(side === 'L' ? 'sbL-pinned' : 'sbR-pinned');
      const btn = side === 'L' ? btnL : btnR;
      if (btn) btn.classList.remove('panel-active');
      if (side === 'L' && pinBtnRef) pinBtnRef.classList.remove('active');
      if (!sideL?.classList.contains('pinned') && !sideR?.classList.contains('pinned')) {
        if (backdrop) backdrop.classList.remove('active');
      }
      try { localStorage.setItem(side === 'L' ? 'ui.sbL.pinned' : 'ui.sbR.pinned', '0'); } catch (e) { /* приватный режим */ }
      // Окна стали шире — изображение нужно вписать заново, иначе оно
      // останется в прежнем масштабе и по краям появятся чёрные поля.
      _relayout();
    }

    // Ссылка на кнопку-булавку: объявлена ниже, а нужна уже здесь.
    let pinBtnRef = null;

    function toggleLeft() {
      if (sideL && sideL.classList.contains('floating-open')) forceClose('L');
      else openLeft();
    }

    function toggleRight() {
      if (sideR && sideR.classList.contains('floating-open')) forceClose('R');
      else openRight();
    }

    // CAPTURE phase — fires BEFORE sidebarBindings.js handlers
    if (btnL) btnL.addEventListener('click', function(e) {
      e.stopImmediatePropagation(); e.preventDefault(); toggleLeft();
    }, true);
    if (btnR) btnR.addEventListener('click', function(e) {
      e.stopImmediatePropagation(); e.preventDefault(); toggleRight();
    }, true);

    // Backdrop click → close
    if (backdrop) backdrop.addEventListener('click', closeSidebars);

    // 5. EDGE HOVER TRIGGERS
    let edgeTimer = null;
    if (edgeL) {
      edgeL.addEventListener('mouseenter', function() { edgeTimer = setTimeout(openLeft, 300); });
      edgeL.addEventListener('mouseleave', function() { clearTimeout(edgeTimer); });
      edgeL.addEventListener('click', function() { clearTimeout(edgeTimer); openLeft(); });
    }
    if (edgeR) {
      edgeR.addEventListener('mouseenter', function() { edgeTimer = setTimeout(openRight, 300); });
      edgeR.addEventListener('mouseleave', function() { clearTimeout(edgeTimer); });
      edgeR.addEventListener('click', function() { clearTimeout(edgeTimer); openRight(); });
    }

    // Escape closes
    document.addEventListener('keydown', function(e) { if (e.key === 'Escape') closeSidebars(); });

    // [Ширина] После перетаскивания края панели — пересчитать холсты
    document.querySelectorAll('.resize-handle').forEach((h) => {
      h.addEventListener('pointerup', () => setTimeout(() => window.dispatchEvent(new Event('resize')), 60));
    });

    // [П.7] Полное имя пресета — во всплывающей подсказке
    try { document.querySelectorAll('.sl-vrt-btn').forEach((b) => { if (!b.title) b.title = b.textContent.trim(); }); } catch (e) { /* не критично */ }

    // ── [5.0.13] Булавка (панель не закрывается) + режим «только серии» ──
    let pinBtn = document.getElementById('btn-pin-left');
    pinBtnRef = pinBtn;
    let maxBtn = document.getElementById('btn-series-max');
    function setPinnedL(on) {
      if (!sideL) return;
      sideL.classList.toggle('pinned', on);
      document.body.classList.toggle('sbL-pinned', on);
      if (pinBtn) pinBtn.classList.toggle('active', on);
      if (on) openLeft();
      try { localStorage.setItem('ui.sbL.pinned', on ? '1' : '0'); } catch (e) { /* приватный режим */ }
    }
    if (pinBtn) pinBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      setPinnedL(!sideL.classList.contains('pinned'));
    });
    if (maxBtn) maxBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      let on = sideL.classList.toggle('series-max');
      maxBtn.classList.toggle('active', on);
      try { localStorage.setItem('ui.sbL.seriesMax', on ? '1' : '0'); } catch (e) { /* приватный режим */ }
    });
    // [10.1.3] Правая панель — такая же встраиваемая, как левая.
    function setPinnedR(on) {
      if (!sideR) return;
      sideR.classList.toggle('pinned', on);
      document.body.classList.toggle('sbR-pinned', on);
      if (on) openRight();
      try { localStorage.setItem('ui.sbR.pinned', on ? '1' : '0'); } catch (e) { /* приватный режим */ }
    }

    try {
      if (sideL && localStorage.getItem('ui.sbL.seriesMax') === '1') { sideL.classList.add('series-max'); if (maxBtn) maxBtn.classList.add('active'); }

      // [10.1.3] Обе панели встраиваются в раскладку ПО УМОЛЧАНИЮ, а правая
      // при этом ещё и перестала наезжать на изображение.
      //
      // Отдельно про левую: там серии, теги и сведения об исследовании —
      // то, с чего работа начинается. Раньше она была скрыта, и о её
      // существовании надо было догадаться по узкой полоске у края.
      //
      // `!== '0'` вместо `=== '1'`: пустое хранилище (первый запуск) означает
      // «по умолчанию», а не «выключено». Кто закрыл панель осознанно — у
      // того лежит '0', и его выбор уважается.
      const pinR = localStorage.getItem('ui.sbR.pinned');
      if (sideR && pinR !== null && pinR !== '0') setPinnedR(true);
      else if (sideR && pinR === null) setPinnedR(true);

      const pinL = localStorage.getItem('ui.sbL.pinned');
      if (pinL !== '0') setPinnedL(true);
    } catch (e) { /* приватный режим */ }

    // 6. TOOLBAR AUTO-COLLAPSE
    if (btnTB && toolbar) {
      // [FIX] Тулбар по умолчанию развёрнут
      btnTB.addEventListener('click', function() { toolbar.classList.toggle('tb-collapsed'); });
    }

    // 7. KEYBOARD SHORTCUTS: [ and ] for sidebars
    document.addEventListener('keydown', function(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === '[') { e.preventDefault(); toggleLeft(); }
      if (e.key === ']') { e.preventDefault(); toggleRight(); }
    });

    // Safety: if sidebarBindings.js sets display:none while panel is open, undo it
    [sideL, sideR].forEach(function(el) {
      if (!el) return;
      new MutationObserver(function() {
        if (el.classList.contains('floating-open') && el.style.display === 'none') {
          el.style.display = '';
        }
      }).observe(el, { attributes: true, attributeFilter: ['style'] });
    });
  }

})();
